"use client"

/**
 * Shared Test Chat Dialog for all agent types.
 *
 * Renders a multi-turn chat UI with tool calls visualization, model info,
 * and superuser log links. Agent-specific context inputs and API endpoints
 * are injected via props.
 */

import { useState, useRef, useEffect, useMemo, type ReactNode } from "react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TooltipProvider,
} from "@/components/ui/tooltip"
import {
  Send,
  Bot,
  User,
  Loader2,
  Wrench,
  HelpCircle,
  Trash2,
  Package,
  AlertCircle,
  CheckCircle,
  ExternalLink,
  MessageSquare,
  RotateCcw,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { SIGNUP_ONBOARDING_TEST_QUESTION_LIMIT } from "@/lib/onboarding/constants"
import { useAuth } from "@/hooks/use-auth"
import { ChatMarkdownBody } from "@/components/ui/markdown-message"
import { ThinkingStatus } from "@/components/agents/shared/thinking-status"
import { ToolActivity } from "@/components/agents/shared/tool-activity"

// ---- Types ----

/**
 * How agent replies are rendered. Spelled out here rather than imported from a
 * channel map: this dialog serves message, email, and onboarding agents, and it
 * must not take a dependency on any one of their domains.
 */
export type TestChatTextFormat = "markdown" | "plain"

export type TestChatExperience =
  | { kind: "standard" }
  | {
      kind: "onboarding"
      openingMessage: string
      suggestedQuestions: string[]
      onRestart?: () => void
    }

export interface TestChatMessage {
  id: string
  role: "user" | "assistant"
  content: string
  timestamp: Date
  toolCalls?: Array<{
    name: string
    args: unknown
    resultPreview: string
  }>
  model?: {
    provider: string
    name: string
  }
  error?: string
  logId?: string
  /**
   * Format this reply was produced under, snapshotted when it arrived. Read at
   * render time instead, and switching the channel mid-session would
   * retroactively reinterpret earlier replies — the chat only clears on an
   * agent change.
   */
  textFormat?: TestChatTextFormat
}

export interface TestChatProps {
  agentId: string
  agentName?: string
  useAiTools?: boolean
  /** API endpoint to POST test messages to */
  apiEndpoint: string
  /** Build request body from message, history, and tools flag. Agent-specific fields go here. */
  buildRequestBody: (params: {
    message: string
    history: Array<{ role: string; content: string }>
    useTools: boolean
  }) => Record<string, unknown>
  /** Optional context inputs rendered above the message input */
  contextInputs?: ReactNode
  /** Placeholder text for the message textarea */
  inputPlaceholder?: string
  /** Tools description shown below the input */
  toolsEnabledLabel?: string
  /** Tools description shown below the input when disabled */
  toolsDisabledLabel?: string
  /** Called with the raw API response data after each successful turn */
  onResponse?: (data: Record<string, unknown>) => void
  /**
   * How agent replies are rendered. Explicit rather than autodetected: this
   * component is shared with the email agents and onboarding, which stay plain.
   */
  textFormat?: TestChatTextFormat
  /** Explicit UI experience; onboarding removes operator-only diagnostics. */
  experience?: TestChatExperience
}

export interface TestChatDialogProps extends TestChatProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const STANDARD_TEST_CHAT_EXPERIENCE: TestChatExperience = { kind: "standard" }

// ---- Component ----

export function TestChatDialog({
  open,
  onOpenChange,
  ...props
}: TestChatDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="flex h-[700px] max-w-2xl flex-col p-0"
        onInteractOutside={(event) => event.preventDefault()}
      >
        <DialogHeader className="border-b px-6 py-4">
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-purple-600" />
            <span>Test Chat</span>
            {props.agentName ? (
              <span className="font-normal text-gray-500">
                - {props.agentName}
              </span>
            ) : null}
          </DialogTitle>
        </DialogHeader>
        <TestChatExperience {...props} className="min-h-0 flex-1" />
      </DialogContent>
    </Dialog>
  )
}

export function TestChat(props: TestChatProps) {
  const mergeOnboardingHeader = props.experience?.kind === "onboarding"

  return (
    <section className="flex h-[min(700px,72vh)] min-h-[460px] flex-col overflow-hidden rounded-2xl border bg-background shadow-sm sm:min-h-[560px]">
      {!mergeOnboardingHeader ? (
        <div className="border-b bg-gradient-to-r from-purple-50 via-background to-blue-50 px-5 py-4">
          <h2 className="flex items-center gap-2 font-semibold">
            <MessageSquare className="h-5 w-5 text-purple-600" />
            <span>Test chat</span>
            {props.agentName ? (
              <span className="font-normal text-muted-foreground">
                with {props.agentName}
              </span>
            ) : null}
          </h2>
        </div>
      ) : null}
      <TestChatExperience
        {...props}
        className="min-h-0 flex-1"
        mergeOnboardingHeader={mergeOnboardingHeader}
      />
    </section>
  )
}

function TestChatExperience({
  agentId,
  agentName,
  useAiTools = false,
  apiEndpoint,
  buildRequestBody,
  contextInputs,
  inputPlaceholder = "Type a test message... (Press Enter to send)",
  toolsEnabledLabel = "AI tools enabled.",
  toolsDisabledLabel = "AI tools are disabled.",
  onResponse,
  textFormat = "plain",
  experience = STANDARD_TEST_CHAT_EXPERIENCE,
  className,
  mergeOnboardingHeader = false,
}: TestChatProps & { className?: string; mergeOnboardingHeader?: boolean }) {
  const { fetchWithAuth, user } = useAuth()
  const isSuperUser = user?.role === "super_user"
  const isOnboarding = experience.kind === "onboarding"
  const openingMessage = isOnboarding ? experience.openingMessage : null
  const [messages, setMessages] = useState<TestChatMessage[]>(() =>
    createInitialMessages(openingMessage),
  )
  const [input, setInput] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [toolsEnabled, setToolsEnabled] = useState(useAiTools)
  const messagesViewportRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const [restartConfirmationOpen, setRestartConfirmationOpen] = useState(false)

  const conversationHistory = useMemo(() => {
    return messages
      .filter((m) => !m.error)
      .map((m) => ({ role: m.role, content: m.content }))
  }, [messages])

  useEffect(() => { setToolsEnabled(useAiTools) }, [useAiTools])

  useEffect(() => {
    setMessages(createInitialMessages(openingMessage))
    setInput("")
  }, [agentId, openingMessage])

  useEffect(() => {
    const viewport = messagesViewportRef.current
    if (!viewport) return
    viewport.scrollTo({
      top: viewport.scrollHeight,
      behavior: "smooth",
    })
  }, [messages, isLoading])

  useEffect(() => {
    const timeout = setTimeout(() => inputRef.current?.focus(), 100)
    return () => clearTimeout(timeout)
  }, [agentId])

  const sendMessage = async (suggestedMessage?: string) => {
    const content = suggestedMessage?.trim() || input.trim()
    if (!content || isLoading) return

    const userMessage: TestChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content,
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setInput("")
    setIsLoading(true)

    try {
      const body = buildRequestBody({
        message: userMessage.content,
        history: conversationHistory,
        useTools: toolsEnabled,
      })

      const response = await fetchWithAuth(apiEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Error: ${response.status}`)
      }

      const data = await response.json()

      const assistantMessage: TestChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: data.response,
        timestamp: new Date(),
        toolCalls: data.toolCalls,
        model: data.model,
        logId: data.logId,
        textFormat,
      }

      setMessages((prev) => [...prev, assistantMessage])
      onResponse?.(data)
    } catch (error: unknown) {
      const errorMessage: TestChatMessage = {
        id: `error-${Date.now()}`,
        role: "assistant",
        content: "",
        timestamp: new Date(),
        error: error instanceof Error ? error.message : "Failed to get response",
      }

      setMessages((prev) => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      void sendMessage()
    }
  }

  const restartChat = () => {
    setMessages(createInitialMessages(openingMessage))
    setInput("")
    setRestartConfirmationOpen(false)
    if (experience.kind === "onboarding") experience.onRestart?.()
    setTimeout(() => inputRef.current?.focus(), 100)
  }

  const requestRestart = () => {
    if (isOnboarding && messages.length > 2) {
      setRestartConfirmationOpen(true)
      return
    }
    restartChat()
  }

  const contextMessageCount = conversationHistory.length

  return (
    <TooltipProvider>
      <div className={cn("flex flex-col overflow-hidden", className)}>
            {/* Toolbar */}
            <div
              className={cn(
                "flex items-center justify-between border-b",
                mergeOnboardingHeader
                  ? "bg-gradient-to-r from-purple-50 via-background to-blue-50 px-3 py-3 sm:px-5 sm:py-4"
                  : "bg-gray-50 px-4 py-2",
              )}
            >
              <div className="flex min-w-0 items-center gap-2">
                {mergeOnboardingHeader ? (
                  <h2 className="flex min-w-0 items-center gap-2 font-semibold">
                    <MessageSquare className="h-5 w-5 shrink-0 text-purple-600" />
                    <span className="shrink-0">Test chat</span>
                    {agentName ? (
                      <span className="truncate font-normal text-muted-foreground">
                        with {agentName}
                      </span>
                    ) : null}
                  </h2>
                ) : null}
                {!isOnboarding && contextMessageCount > 0 && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    {contextMessageCount} <span>in context</span>
                  </Badge>
                )}
                {!mergeOnboardingHeader ? <TestChatHelp /> : null}
              </div>
              <div className="flex shrink-0 items-center gap-1 sm:gap-4">
                {mergeOnboardingHeader ? <TestChatHelp /> : null}
                {!isOnboarding ? <div className="flex items-center gap-2">
                  <Wrench className={cn("w-4 h-4", toolsEnabled ? "text-green-600" : "text-gray-400")} />
                  <Label htmlFor="tools-toggle" className="text-sm text-gray-600">AI Tools</Label>
                  <Switch
                    id="tools-toggle"
                    checked={toolsEnabled}
                    onCheckedChange={setToolsEnabled}
                    className="data-[state=checked]:bg-green-600"
                  />
                </div> : null}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={requestRestart}
                      disabled={messages.length === 0 || isLoading}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      {isOnboarding ? (
                        <>
                          <RotateCcw className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Start over</span>
                        </>
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{isOnboarding ? "Restart with the opening message" : "Clear chat and context"}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>

            {/* Messages Area */}
            <ScrollArea
              viewportRef={messagesViewportRef}
              className="flex-1 p-3 sm:p-4"
            >
              {messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <Bot className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-sm text-center">Send a test message to see how the AI responds.</p>
                  <p className="text-xs text-gray-400 mt-2 text-center max-w-xs">
                    Uses this agent&apos;s system prompt and configuration.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={cn("flex gap-3", message.role === "user" ? "justify-end" : "justify-start")}
                    >
                      {message.role === "assistant" && (
                        <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                          <Bot className="w-4 h-4 text-purple-600" />
                        </div>
                      )}
                      <div
                        className={cn(
                          "max-w-[80%] rounded-lg px-4 py-2 overflow-hidden",
                          message.role === "user"
                            ? "bg-blue-600 text-white"
                            : message.error
                              ? "bg-red-50 border border-red-200"
                              : "bg-gray-100 text-gray-900"
                        )}
                      >
                        {message.error ? (
                          <div className="flex items-center gap-2 text-red-600">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            <span className="text-sm break-words">{message.error}</span>
                          </div>
                        ) : (
                          <>
                            {/* react-markdown emits p/ul/pre/blockquote, which
                                cannot live inside a <p> — the branches are
                                siblings, not nested. */}
                            {message.role === "assistant" && message.textFormat === "markdown" ? (
                              <div className="text-sm break-words" style={{ overflowWrap: "anywhere" }}>
                                <ChatMarkdownBody text={message.content} />
                              </div>
                            ) : (
                              <p className="text-sm whitespace-pre-wrap break-words" style={{ overflowWrap: "anywhere" }}>
                                {message.content}
                              </p>
                            )}

                            {isOnboarding && message.toolCalls && message.toolCalls.length > 0 ? (
                              <ToolActivity calls={message.toolCalls} />
                            ) : null}

                            {/* Tool Calls */}
                            {!isOnboarding && message.toolCalls && message.toolCalls.length > 0 && (
                              <div className="mt-3 pt-3 border-t border-gray-200 space-y-2">
                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                  <Wrench className="w-3 h-3" />
                                  <span>Tools used:</span>
                                </div>
                                {message.toolCalls.map((tc, idx) => (
                                  <div key={idx} className="flex items-start gap-2 text-xs bg-white rounded p-2 border overflow-hidden">
                                    <Package className="w-3 h-3 text-blue-500 mt-0.5 flex-shrink-0" />
                                    <div className="flex-1 min-w-0 overflow-hidden">
                                      <span className="font-medium text-blue-600">{tc.name}</span>
                                      <span className="text-gray-500 ml-1 break-all" style={{ overflowWrap: "anywhere" }}>
                                        ({JSON.stringify(tc.args)})
                                      </span>
                                      {tc.resultPreview && (
                                        <p className="text-gray-600 mt-1 break-words" style={{ overflowWrap: "anywhere" }}>
                                          {tc.resultPreview}<span>...</span>
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Model info + log link.
                                Model badge (`openai: gpt-5-mini`) is super-user only
                                — tenant admins don't need to see which provider /
                                model the platform calls under the hood. Log link
                                stays visible whenever the server returned a logId
                                (behaviour pre-regression). */}
                            {!isOnboarding && ((isSuperUser && message.model) || message.logId) && (
                              <div className="mt-2 flex items-center gap-2">
                                {isSuperUser && message.model && (
                                  <Badge variant="outline" className="text-xs bg-white/50">
                                    {message.model.provider}<span>:</span> {message.model.name}
                                  </Badge>
                                )}
                                {message.logId && (
                                  <a
                                    href={`/?page=ai-logs&log_id=${message.logId}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    <ExternalLink className="w-3 h-3" />
                                    <span>View Log</span>
                                  </a>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                      {message.role === "user" && (
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-blue-600" />
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Loading Indicator */}
                  {isLoading && (
                    <div className="flex gap-3 justify-start">
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Bot className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="bg-gray-100 rounded-lg px-4 py-2">
                        <ThinkingStatus withTools={toolsEnabled} />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </ScrollArea>

            {/* Input Area */}
            <div className="border-t bg-gray-50 p-3 sm:p-4">
              {!isOnboarding && contextInputs ? <div className="mb-3">{contextInputs}</div> : null}
              {experience.kind === "onboarding" && experience.suggestedQuestions.length > 0 ? (
                <div
                  className="mb-3 grid grid-cols-3 gap-2"
                  data-testid="onboarding-test-questions"
                >
                  {experience.suggestedQuestions
                    .slice(0, SIGNUP_ONBOARDING_TEST_QUESTION_LIMIT)
                    .map((question) => (
                      <Button
                        key={question}
                        type="button"
                        variant="outline"
                        size="sm"
                        title={question}
                        disabled={isLoading}
                        onClick={() => void sendMessage(question)}
                        className="h-full min-h-9 w-full min-w-0 whitespace-normal rounded-lg px-3 py-2 text-left text-xs leading-tight"
                      >
                        <span className="line-clamp-2">{question}</span>
                      </Button>
                    ))}
                </div>
              ) : null}
              <div className="flex gap-2">
                <Textarea
                  ref={inputRef}
                  placeholder={inputPlaceholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isLoading}
                  rows={2}
                  className="resize-none text-sm focus-visible:border-blue-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                />
                <Button
                  type="button"
                  aria-label="Send test message"
                  onClick={() => void sendMessage()}
                  disabled={!input.trim() || isLoading}
                  className="h-auto self-stretch bg-purple-600 px-4 hover:bg-purple-700"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              {!isOnboarding ? <div className="mt-2 flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  {toolsEnabled ? (
                    <span className="flex items-center gap-1">
                      <CheckCircle className="w-3 h-3 text-green-500" />
                      {toolsEnabledLabel}
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      <AlertCircle className="w-3 h-3 text-yellow-500" />
                      {toolsDisabledLabel}
                    </span>
                  )}
                </p>
                {contextMessageCount > 0 && (
                  <p className="text-xs text-blue-600"><span>Context:</span> {contextMessageCount} <span>messages</span></p>
                )}
              </div> : null}
            </div>
      </div>
      <AlertDialog
        open={restartConfirmationOpen}
        onOpenChange={setRestartConfirmationOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Start this test over?</AlertDialogTitle>
            <AlertDialogDescription>
              This clears the test conversation and handoff state. Your saved
              agent prompt and settings will stay unchanged.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep conversation</AlertDialogCancel>
            <AlertDialogAction onClick={restartChat}>Start over</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  )
}

function TestChatHelp() {
  return (
    <Tooltip>
      <TooltipTrigger asChild aria-label="About test chat">
        <HelpCircle className="h-4 w-4 cursor-help text-muted-foreground" aria-hidden />
      </TooltipTrigger>
      <TooltipContent className="max-w-[min(20rem,calc(100vw-2rem))]">
        <p>
          Test how the AI responds using this agent&apos;s system prompt and settings.
          The AI remembers conversation context until you reset it.
        </p>
      </TooltipContent>
    </Tooltip>
  )
}

function createInitialMessages(openingMessage: string | null): TestChatMessage[] {
  if (!openingMessage) return []

  return [{
    id: "onboarding-opening-message",
    role: "assistant",
    content: openingMessage,
    timestamp: new Date(),
    textFormat: "plain",
  }]
}
